import { useAuth } from '@/@diffrag/providers/user.provider'
import { api } from '@/api'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import BrowserCookie from 'js-cookie'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useLocalStorage } from '@/@diffrag/hooks/use-storage'
import { useLayoutEffect } from 'react'
import { AdminRole, type AdminJwtUser } from '@/constants/adminRole'

const SERVICE_DOMAIN =
  process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test'
    ? import.meta.env.VITE_SERVICE_DOMAIN
    : 'http://localhost:3000'

const formSchema = z.object({
  username: z.string().min(1, { message: '아이디를 입력해주세요.' }),
  password: z.string().min(1, { message: '비밀번호를 입력해주세요.' }),
  remember: z.boolean(),
})

export function Signin() {
  const navigate = useNavigate()
  const { toast, dismiss } = useToast()
  const auth = useAuth()

  const [storagedId, setStoragedId, removeStoragedId] = useLocalStorage<string>(
    {
      key: 'admin-user-name',
    },
  )

  const [autoLogin, setAutoLogin, removeAutoLogin] = useLocalStorage<boolean>({
    key: 'admin-auto-login',
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: storagedId ?? '',
      password: '',
      remember: !!autoLogin,
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const res = await api().admin.login({
        username: values.username,
        password: values.password,
      })

      if (values.remember) {
        setStoragedId(values.username)
        setAutoLogin(true)
      } else {
        removeStoragedId()
        removeAutoLogin()
      }

      console.log(res, SERVICE_DOMAIN)

      BrowserCookie.set('admin_refresh_token', res.refreshToken, {
        secure: true,
        expires: new Date(Date.now() + res.refreshTokenExpiresInMilliseconds),
        domain:
          process.env.NODE_ENV === 'development'
            ? 'localhost'
            : SERVICE_DOMAIN.replace('http://', '').replace('https://', ''),
        sameSite: 'lax',
        path: '/',
      })

      BrowserCookie.set('admin_access_token', res.accessToken, {
        secure: true,
        expires: new Date(Date.now() + res.accessTokenExpiresInMilliseconds),
        domain:
          process.env.NODE_ENV === 'development'
            ? 'localhost'
            : SERVICE_DOMAIN.replace('http://', '').replace('https://', ''),
        sameSite: 'lax',
        path: '/',
      })

      const synced = auth.sync() as AdminJwtUser | undefined
      if (synced) {
        dismiss()
        navigate(
          synced.role === AdminRole.PARTNER_ADMIN ? '/referral-stats' : '/main',
          { replace: true },
        )
      } else {
        toast({
          variant: 'destructive',
          description: '권한이 없습니다.',
        })
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        description: '아이디 혹은 비밀번호가 틀립니다.',
      })
    }
  }

  useLayoutEffect(() => {
    if (storagedId && autoLogin) {
      form.setValue('username', storagedId)
      form.setValue('remember', true)
    }
  }, [storagedId, autoLogin])

  return (
    <div className="h-dvh w-full bg-[#F1F5F9] p-5">
      <div className="mx-auto flex size-full max-w-[382px] flex-col justify-center gap-6">
        {/* <Kurita className="justify-self-start" /> */}
        <div className=" w-full rounded-xl border border-[#E2E8F0] bg-white  p-6">
          <p className="mb-6 text-[24px] font-bold">로그인</p>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>아이디</FormLabel>
                    <FormControl>
                      <Input placeholder="아이디를 입력하세요" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="h-6" />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>비밀번호</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="비밀번호를 입력하세요"
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="h-5" />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={form.watch('remember')}
                  onCheckedChange={() => {
                    form.setValue('remember', !form.watch('remember'))
                    setAutoLogin(form.watch('remember'))
                  }}
                />
                <Label htmlFor="remember">로그인 정보 기억하기</Label>
              </div>
              <div className="h-7" />
              <Button type="submit" className="mb-4 w-full">
                로그인
              </Button>
              <p className="text-center text-[14px] text-slate-900">
                비밀번호 분실시, 관리자에게 문의하세요.
              </p>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
